import {
  GetPackingListDelivery,
  GetPackingListOrderLine,
  GetPackingListRoute,
} from 'src/graphql/types/delivery';

const CHILLED_BULK_GROUP_ID = 'chilled-bulk';
const BULK_GROUP_ID = 'bulk';
const AMBIENT_GROUP_ID = 'ambient';
const ORDER_GROUP_ID = 'order';

export enum GroupBy {
  item = 'item',
  order = 'order',
}

export type PackingListRowType = {
  route?: {
    id: string;
    name: string;
  };
  group?: {
    id: string;
    name: string;
  };
  item: {
    id?: string;
    name: string;
    quantity: string;
    refrigerationState: string;
  };
  itemLevelTracking: boolean;
  isRedelivery?: boolean;
  fulfillmentQuantity: number;
  orderUnitQuantity: number;
  remainingQuantity: number;
  deliveredQuantity: number;
};

export type BulkItemRow = {
  id: string;
  name: string;
  quantityType: string;
  refrigerationState: string;
  itemLevelTracking: boolean;
  orderUnitQuantity: number;
  deliveredQuantity: number;
  fulfillmentQuantity: number;
};

export type RoutesFilterOption = {
  id: string;
  name: string;
  selected: boolean;
};

export const addOrUpdatePackingListRow = ({
  rowsToUpdate,
  orderLine,
  route,
  group,
  itemLevelTracking,
  isRedelivery,
}: {
  rowsToUpdate: PackingListRowType[];
  orderLine: GetPackingListOrderLine;
  route: {
    id: string;
    name: string;
  };
  group: {
    id: string;
    name: string;
  };
  itemLevelTracking: boolean;
  isRedelivery?: boolean;
}): PackingListRowType[] => {
  const foundRowIndex = rowsToUpdate.findIndex(
    (row) =>
      row.route?.id === route.id &&
      row.group?.id === group.id &&
      row.item.id === orderLine.sku.item?.id,
  );
  if (foundRowIndex < 0) {
    rowsToUpdate.push({
      route,
      group,
      item: {
        id: orderLine.sku.item?.id,
        name: orderLine.sku.name,
        quantity: getQuantityStr(orderLine),
        refrigerationState: orderLine.sku.refrigerationState,
      },
      itemLevelTracking,
      isRedelivery,
      fulfillmentQuantity: orderLine.fulfillmentQuantity ?? 0,
      orderUnitQuantity: orderLine.orderUnitQuantity,
      remainingQuantity:
        (orderLine.fulfillmentQuantity ?? 0) -
        (orderLine.deliveredQuantity ?? 0),
      deliveredQuantity: orderLine.deliveredQuantity ?? 0,
    });
  } else {
    const foundRow = rowsToUpdate[foundRowIndex];
    rowsToUpdate[foundRowIndex] = {
      ...foundRow,
      orderUnitQuantity:
        (orderLine.orderUnitQuantity ?? 0) + (foundRow.orderUnitQuantity ?? 0),
      deliveredQuantity:
        (orderLine.deliveredQuantity ?? 0) + (foundRow.deliveredQuantity ?? 0),
      fulfillmentQuantity:
        (orderLine.fulfillmentQuantity ?? 0) +
        (foundRow.fulfillmentQuantity ?? 0),
    };
  }
  return rowsToUpdate;
};

export const getAbbreviation = (name: string): string =>
  name
    .split(' ')
    .map((subName) => subName[0])
    .join('');

export const getAbbreviationRoute = (name: string): string => {
  const nameArray = name.split(' ');
  return nameArray
    .map((subName, index) => {
      if (index === nameArray.length - 1) return subName;
      else return subName[0];
    })
    .join('');
};

export const getOrderGroupName = ({
  sequenceNumber,
  isRedelivery,
  vendorName,
  locationName,
}: {
  sequenceNumber?: string;
  isRedelivery?: boolean;
  vendorName: string;
  locationName?: string;
}): string => {
  const vendorAbb = getAbbreviation(vendorName);
  return `${isRedelivery ? 'Re ' : ''}S${
    Number(sequenceNumber) + 1
  } ${vendorAbb}: ${locationName}`;
};

export const getUnitWeightLb = (orderLine: {
  isPounds?: boolean;
  isMarketPriced?: boolean;
  unitWeightLb?: number | null;
  orderedWeightLb?: number | null;
  orderUnitQuantity?: number;
}): number | null => {
  if (orderLine.unitWeightLb) return orderLine.unitWeightLb;
  if (
    orderLine.isPounds &&
    !orderLine.isMarketPriced &&
    orderLine.orderedWeightLb &&
    orderLine.orderUnitQuantity
  ) {
    return orderLine.orderedWeightLb / orderLine.orderUnitQuantity;
  }
  return null;
};

export const getQuantityStr = (orderLine: {
  quantityType: string;
  itemsPerLot: number;
}): string => {
  const quantityTypeFormatted =
    orderLine.quantityType.charAt(0).toUpperCase() +
    orderLine.quantityType.slice(1);
  const quantity =
    quantityTypeFormatted === 'Case'
      ? quantityTypeFormatted + ' of ' + orderLine.itemsPerLot
      : 'Each';
  return quantity;
};

export type PackingRowGroups = {
  chilledRows: PackingListRowType[];
  itemRows: PackingListRowType[];
  bulkRows: PackingListRowType[];
  ambientRows: PackingListRowType[];
  orderRows: PackingListRowType[];
};

// Buckets every order line across the given routes into the five packing-list
// groups (chilled / bulk / ambient / item / order), honoring the per-category
// item-vs-order grouping toggles. Shared by the on-screen tables (fed the
// route-filtered set) and Print All (fed every route) so the two never drift.
/* eslint-disable max-nested-callbacks */
export const buildPackingRows = ({
  routes,
  isRedelivery,
  chilledGroup,
  bulkGroup,
  ambientGroup,
  hideNonItemLevelTrackedOrders,
  separateBulkAmbient,
}: {
  routes: GetPackingListRoute[];
  isRedelivery?: boolean;
  chilledGroup: GroupBy;
  bulkGroup: GroupBy;
  ambientGroup: GroupBy;
  hideNonItemLevelTrackedOrders: boolean;
  separateBulkAmbient: boolean;
}): PackingRowGroups => {
  let chilledRows: PackingListRowType[] = [];
  let itemRows: PackingListRowType[] = [];
  let bulkRows: PackingListRowType[] = [];
  let ambientRows: PackingListRowType[] = [];
  let orderRows: PackingListRowType[] = [];

  routes.forEach((route) => {
    const stops = isRedelivery
      ? route.redeliveryStops
      : route.stops?.filter((stop) => stop.__typename === 'Delivery') ?? [];
    (stops as GetPackingListDelivery[]).forEach((stop) => {
      const filteredOrders = hideNonItemLevelTrackedOrders
        ? stop.orders?.filter((order) => order.itemLevelTracking)
        : stop.orders;
      filteredOrders?.forEach((order) => {
        const orderGroup = {
          id: `${ORDER_GROUP_ID}-${order.id}`,
          name: getOrderGroupName({
            sequenceNumber: stop.sequenceNumber,
            isRedelivery,
            vendorName: order.vendor.legalName,
            locationName: stop.location?.businessName,
          }),
        };
        order.orderLines.forEach((orderLine) => {
          const rowData = {
            orderLine,
            route: { id: route.id, name: route.name },
            itemLevelTracking: order.itemLevelTracking,
            isRedelivery,
          };
          if (orderLine.sku.refrigerationState !== 'room_temperature') {
            // chilled item
            if (chilledGroup === GroupBy.item) {
              chilledRows = addOrUpdatePackingListRow({
                ...rowData,
                rowsToUpdate: chilledRows,
                group: {
                  id: `${CHILLED_BULK_GROUP_ID}-${order.vendor.id}`,
                  name: `Chilled: ${getAbbreviation(order.vendor.legalName)}`,
                },
              });
            } else {
              orderRows = addOrUpdatePackingListRow({
                ...rowData,
                rowsToUpdate: orderRows,
                group: orderGroup,
              });
            }
          } else if (orderLine.bulkPickedItem) {
            // bulk item
            if (bulkGroup === GroupBy.item) {
              if (separateBulkAmbient) {
                bulkRows = addOrUpdatePackingListRow({
                  ...rowData,
                  rowsToUpdate: bulkRows,
                  group: {
                    id: `${BULK_GROUP_ID}-${order.vendor.id}`,
                    name: `Bulk: ${getAbbreviation(order.vendor.legalName)}`,
                  },
                });
              } else {
                itemRows = addOrUpdatePackingListRow({
                  ...rowData,
                  rowsToUpdate: itemRows,
                  group: {
                    id: `${BULK_GROUP_ID}-${order.vendor.id}`,
                    name: `Item: ${getAbbreviation(order.vendor.legalName)}`,
                  },
                });
              }
            } else {
              orderRows = addOrUpdatePackingListRow({
                ...rowData,
                rowsToUpdate: orderRows,
                group: orderGroup,
              });
            }
          } else {
            // ambient item
            if (ambientGroup === GroupBy.item) {
              if (separateBulkAmbient) {
                ambientRows = addOrUpdatePackingListRow({
                  ...rowData,
                  rowsToUpdate: ambientRows,
                  group: {
                    id: `${AMBIENT_GROUP_ID}-${order.vendor.id}`,
                    name: `Ambient: ${getAbbreviation(order.vendor.legalName)}`,
                  },
                });
              } else {
                itemRows = addOrUpdatePackingListRow({
                  ...rowData,
                  rowsToUpdate: itemRows,
                  group: {
                    id: `${BULK_GROUP_ID}-${order.vendor.id}`,
                    name: `Item: ${getAbbreviation(order.vendor.legalName)}`,
                  },
                });
              }
            } else {
              orderRows = addOrUpdatePackingListRow({
                ...rowData,
                rowsToUpdate: orderRows,
                group: orderGroup,
              });
            }
          }
        });
      });
    });
  });

  return { chilledRows, itemRows, bulkRows, ambientRows, orderRows };
};
/* eslint-enable max-nested-callbacks */
